import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useEstaOnline } from '@/features/auth/AuthContext';
import { capturaApi, mensagemErroCaptura } from '@/features/captura/api/capturaApi';
import { prepararImagemOcr } from '@/features/captura/comprimirImagem';
import {
  avisoDeQualidade,
  medirQualidadeFoto,
  type AvisoQualidade,
} from '@/features/captura/qualidadeFoto';
import { ehDispositivoMovelComCamera } from '@/features/captura/dispositivo';
import {
  formatarPlacaExibicao,
  normalizarPlacaCaptura,
} from '@/features/captura/aplicarCamposCapturados';
import type { LeituraImagemDTO } from '@/features/captura/tipos';
import { IconCamera } from '@/features/captura/components/icones';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import '@/features/captura/captura.css';

export type StatusLeituraPlaca = {
  tipo: 'lendo' | 'ok' | 'aviso' | 'erro';
  mensagem: string;
} | null;

type Props = {
  /** Mesmas condições do campo placa (cliente escolhido etc.). */
  desabilitado?: boolean;
  /** Celular/tablet: a placa lida pela câmera do próprio aparelho. */
  aoLerPlaca: (leitura: { placa: string; confianca: LeituraImagemDTO['confianca'] }) => void;
  /** Computador: quem usa abre o `ModalCapturaCelular` (modo `placa`). */
  aoPedirQr: () => void;
  /** Chamado no clique, antes de abrir câmera/QR (ex.: desmarcar "Sem placa"). */
  aoAcionar?: () => void;
  /** "Lendo placa…", erros e o aviso de confiança baixa — a tela mostra onde couber. */
  aoMudarStatus?: (status: StatusLeituraPlaca) => void;
  /** Força o comportamento (testes). Padrão: detecta o aparelho. */
  modo?: 'camera' | 'qr';
};

const ERROS_OCR: Partial<Record<number, string>> = {
  503: 'Leitura automática indisponível — digite a placa.',
  413: 'A foto ficou grande demais. Tente de novo, mais perto da placa.',
  429: 'Limite de leituras atingido. Digite a placa no campo.',
};

/**
 * Ícone de câmera ao lado do campo Placa. No celular/tablet lê a placa de
 * uma foto (OCR no servidor) — tirada na hora ou escolhida da galeria; no
 * computador pede o QR para ler com o celular. Requer internet nos dois
 * alvos.
 */
export function BotaoCameraPlaca({
  desabilitado,
  aoLerPlaca,
  aoPedirQr,
  aoAcionar,
  aoMudarStatus,
  modo,
}: Props) {
  const online = useEstaOnline();
  const [lendo, setLendo] = useState(false);
  // Foto escura/borrada esperando a decisão: tirar de novo ou enviar assim.
  const [pendente, setPendente] = useState<{ arquivo: Blob; aviso: AvisoQualidade } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const usaCamera = useMemo(
    () => (modo ? modo === 'camera' : ehDispositivoMovelComCamera()),
    [modo],
  );

  const bloqueado = Boolean(desabilitado) || !online || lendo;
  const dica = !online
    ? 'Requer internet'
    : lendo
      ? 'Lendo placa…'
      : usaCamera
        ? 'Ler placa: tirar foto ou escolher da galeria'
        : 'Ler placa pelo celular (QR code)';

  function acionar() {
    if (bloqueado) return;
    aoAcionar?.();
    if (usaCamera) {
      inputRef.current?.click();
    } else {
      aoPedirQr();
    }
  }

  async function aoEscolherFoto(e: ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    // Libera o input para fotografar de novo a mesma placa.
    e.target.value = '';
    if (!arquivo) return;
    setLendo(true);
    aoMudarStatus?.({ tipo: 'lendo', mensagem: 'Lendo placa…' });
    // Lente embaçada ou garagem sem luz: avisa antes de gastar a leitura.
    // Nunca bloqueia — sem medida (aparelho sem canvas) segue direto.
    const qualidade = await medirQualidadeFoto(arquivo);
    const aviso = qualidade ? avisoDeQualidade(qualidade) : null;
    if (aviso) {
      setLendo(false);
      aoMudarStatus?.(null);
      setPendente({ arquivo, aviso });
      return;
    }
    await lerPlaca(arquivo);
  }

  // Abre o seletor dentro do próprio clique: o iPhone só abre câmera ou
  // galeria a partir de um toque do usuário, não depois de um `await`.
  function tirarDeNovo() {
    setPendente(null);
    inputRef.current?.click();
  }

  function enviarAssimMesmo() {
    const arquivo = pendente?.arquivo;
    setPendente(null);
    if (arquivo) void lerPlaca(arquivo);
  }

  async function lerPlaca(arquivo: Blob) {
    setLendo(true);
    aoMudarStatus?.({ tipo: 'lendo', mensagem: 'Lendo placa…' });
    try {
      const imagemBase64 = await prepararImagemOcr(arquivo);
      const leitura = await capturaApi.lerImagem({ alvo: 'placa', imagemBase64 });
      const placa = normalizarPlacaCaptura(leitura?.placa);
      if (placa.length !== 7) {
        aoMudarStatus?.({
          tipo: 'erro',
          mensagem: 'Não deu para ler a placa. Tente de novo mais perto ou digite no campo.',
        });
        return;
      }
      aoLerPlaca({ placa, confianca: leitura.confianca });
      aoMudarStatus?.(
        leitura.confianca === 'baixa'
          ? { tipo: 'aviso', mensagem: `Confira a placa: ${formatarPlacaExibicao(placa)}` }
          : { tipo: 'ok', mensagem: `Placa lida: ${formatarPlacaExibicao(placa)}` },
      );
    } catch (erro) {
      aoMudarStatus?.({
        tipo: 'erro',
        mensagem: mensagemErroCaptura(
          erro,
          'Não foi possível ler a placa. Digite no campo.',
          ERROS_OCR,
        ),
      });
    } finally {
      setLendo(false);
    }
  }

  return (
    <>
      <span
        className="captura-camera__envoltorio"
        data-tooltip-id="icon-tip"
        data-tooltip-content={dica}
      >
        <button
          type="button"
          className={`btn captura-camera${lendo ? ' captura-camera--lendo' : ''}`}
          aria-label="Ler placa pela câmera"
          aria-busy={lendo || undefined}
          disabled={bloqueado}
          onClick={acionar}
        >
          <IconCamera />
        </button>
        {/*
          Sem `capture`: o próprio sistema oferece "Tirar foto" ou a galeria
          (iPhone: Fototeca/Tirar Foto; Android: Câmera/Arquivos). Quem
          fotografou vários carros antes sobe a foto que já está no celular.
        */}
        {usaCamera && (
          <input
            ref={inputRef}
            className="captura-input-arquivo"
            type="file"
            accept="image/*"
            tabIndex={-1}
            aria-hidden="true"
            data-testid="captura-input-placa"
            onChange={(e) => void aoEscolherFoto(e)}
          />
        )}
      </span>
      <Modal
        aberto={pendente !== null}
        titulo={pendente?.aviso.titulo ?? ''}
        onFechar={() => setPendente(null)}
        rodape={
          <>
            <Button onClick={tirarDeNovo}>Tirar de novo</Button>
            <Button variante="primary" onClick={enviarAssimMesmo}>
              Enviar assim mesmo
            </Button>
          </>
        }
      >
        <p className="captura-aviso-qualidade" role="alert">
          {pendente?.aviso.mensagem}
        </p>
      </Modal>
    </>
  );
}
